import { z } from 'zod';

/**
 * Assinatura eletrônica (ADR 0088): peça comum, hoje pela ZapSign. O envio é cobrado por documento
 * criado, mesmo sem assinatura; por isso só sai por ação explícita de quem conduz.
 */

/** Situação do envio. */
export const ESIGN_STATUSES = [
  'sending',
  'pending',
  'signed',
  'refused',
  'cancelled',
  'failed',
] as const;
export type EsignStatus = (typeof ESIGN_STATUSES)[number];
/** Envio aberto: um por vez para cada registro de origem. */
export const ESIGN_OPEN_STATUSES: readonly EsignStatus[] = ['sending', 'pending'];

export const ESIGN_SIGNER_STATUSES = ['pending', 'viewed', 'signed', 'refused'] as const;
export type EsignSignerStatus = (typeof ESIGN_SIGNER_STATUSES)[number];

/**
 * Como o signatário confirma quem é (decisão de 28/10/2026): assinatura na tela com código por
 * e-mail, por padrão; certificado digital quando o Jurídico marcar. Sem SMS nem WhatsApp.
 */
export const ESIGN_AUTH_MODES = ['email_token', 'certificate'] as const;
export type EsignAuthMode = (typeof ESIGN_AUTH_MODES)[number];

/** Tipos de arquivo aceitos pela ZapSign (até 10 MB). */
export const ESIGN_CONTENT_TYPES: Record<string, 'pdf' | 'docx'> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};
export const ESIGN_MAX_BYTES = 10 * 1024 * 1024;

/** Motivo gravado quando o documento foi excluído direto na plataforma. */
export const ESIGN_DELETED_ON_PROVIDER = 'deleted_on_provider';

export const esignSendInput = z.object({
  /** Anexo da solicitação que vai para assinatura (a versão aprovada do documento). */
  attachmentId: z.uuid(),
  authMode: z.enum(ESIGN_AUTH_MODES).default('email_token'),
  /** Assinaturas em sequência (na ordem dos participantes); padrão: todos ao mesmo tempo. */
  sequential: z.boolean().default(false),
  /** Registro sigiloso: confirmação explícita de que o documento pode ir à plataforma. */
  confirmSensitive: z.boolean().default(false),
});
export type EsignSendInput = z.infer<typeof esignSendInput>;

export const esignCancelInput = z.object({ reason: z.string().trim().min(3).max(500) });

export interface EsignSignerView {
  id: string;
  position: number;
  role: string;
  fullName: string;
  email: string;
  status: EsignSignerStatus;
  signedAt: string | null;
}

export interface EsignEnvelopeView {
  id: string;
  status: EsignStatus;
  environment: 'sandbox' | 'production';
  title: string;
  authMode: EsignAuthMode;
  sequential: boolean;
  sensitiveConfirmed: boolean;
  failure: string | null;
  cancelReason: string | null;
  sentBy: { id: string; name: string | null };
  sentAt: string;
  completedAt: string | null;
  lastSyncedAt: string | null;
  /** Documento assinado guardado no servidor, pronto para juntar aos anexos. */
  signedReady: boolean;
  signedAttachmentId: string | null;
  signers: EsignSignerView[];
}
